import type { ENudgeId, INudgePersistenceConfig } from './types';
import { areToursDisabled } from '../persistence';

const STORAGE_KEY_PREFIX = 'nudge_dismissed_';

/**
 * Resolves the storage key for a given nudge.
 *
 * @param nudgeId - Nudge identifier
 * @param config - Optional persistence config with custom key
 * @returns The resolved storage key
 */
function getStorageKey(
  nudgeId: ENudgeId,
  config?: INudgePersistenceConfig,
): string {
  return config?.key ?? `${STORAGE_KEY_PREFIX}${nudgeId}`;
}

/**
 * Checks whether a nudge has been dismissed by the user.
 * Also returns true when all tours/nudges are globally disabled.
 *
 * @param nudgeId - Nudge identifier
 * @param config - Optional persistence config
 * @returns True if the nudge has been dismissed
 */
export function isNudgeDismissed(
  nudgeId: ENudgeId,
  config?: INudgePersistenceConfig,
): boolean {
  if (areToursDisabled()) {
    return true;
  }

  const strategy = config?.strategy ?? 'localStorage';

  if (strategy === 'none') {
    return false;
  }

  try {
    return localStorage.getItem(getStorageKey(nudgeId, config)) === 'true';
  } catch {
    return false;
  }
}

/**
 * Marks a nudge as dismissed in localStorage.
 *
 * @param nudgeId - Nudge identifier
 * @param config - Optional persistence config
 */
export function markNudgeDismissed(
  nudgeId: ENudgeId,
  config?: INudgePersistenceConfig,
): void {
  const strategy = config?.strategy ?? 'localStorage';

  if (strategy === 'none') {
    return;
  }

  try {
    localStorage.setItem(getStorageKey(nudgeId, config), 'true');
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Resets a nudge's dismissed state so it can be shown again.
 *
 * @param nudgeId - Nudge identifier
 * @param config - Optional persistence config
 */
export function resetNudgeDismissal(
  nudgeId: ENudgeId,
  config?: INudgePersistenceConfig,
): void {
  const strategy = config?.strategy ?? 'localStorage';

  if (strategy === 'none') {
    return;
  }

  try {
    localStorage.removeItem(getStorageKey(nudgeId, config));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}
